import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Button,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import Select from "components/Shared/TranslatedSelect"
import { getAllBranchs } from "store/actions/usersAction"
import useCrossModuleFilterPermissions from "hooks/useCrossModuleFilterPermissions"
import { handleFileChange } from "components/Shared/fileHandlers"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import PhoneNumberField from "components/Shared/PhoneNumberField"
import { itemHeight, itemMaxSize, itemWidth } from "components/Common/ImageSize"
import { EMAIL_TOTAL_MAX_LENGTH } from "utils/emailValidation"
import { normalizePhoneValue } from "utils/phoneUtils"
import {
  RIDER_PROFILE_URL_MAX_LENGTH,
  RIDER_STATUS_REASON_MAX_LENGTH,
  validateRiderBranch,
  validateRiderEmail,
  validateRiderForm,
  validateRiderName,
  validateRiderPhone,
  validateRiderProfilePhotoUrl,
  validateRiderProfilePhotoUrlDimensions,
  validateRiderStatusReason,
  REASON_REQUIRED_STATUSES,
  hasRiderFormErrors,
} from "utils/riderFormValidation"
import { getRiderPendingOrdersCount } from "services/deliveryPartnerService"

const STATUS_OPTIONS = ["Pending", "Approved", "Rejected", "Suspended"]

const SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
}

const riderSelectBaseStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: base => ({
    ...base,
    maxHeight: 220,
    overflowY: "auto",
    overflowX: "auto",
  }),
  option: base => ({
    ...base,
    whiteSpace: "nowrap",
    width: "max-content",
    minWidth: "100%",
  }),
  singleValue: base => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
}

const getRiderSelectStyles = (menuWidth, hasError) => ({
  ...riderSelectBaseStyles,
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? "#f46a6a" : state.isFocused ? base.borderColor : base.borderColor,
    boxShadow: hasError ? "0 0 0 0.15rem rgba(244, 106, 106, 0.25)" : base.boxShadow,
  }),
  menu: base => ({
    ...riderSelectBaseStyles.menu(base),
    ...(menuWidth
      ? { width: menuWidth, minWidth: menuWidth, maxWidth: menuWidth }
      : {}),
  }),
})

const RiderFormSelect = ({
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  classNamePrefix = "rider-form-select",
  isSearchable = false,
  hasError = false,
}) => {
  const { t } = useTranslation()
  const fieldRef = useRef(null)
  const [menuWidth, setMenuWidth] = useState(null)
  const selectedOption = options.find(option => option.value === value) || null
  const styleConfig = useMemo(
    () => getRiderSelectStyles(menuWidth, hasError),
    [menuWidth, hasError]
  )

  return (
    <div ref={fieldRef} className="rider-form-select-field">
      <Select
        value={selectedOption}
        onChange={option => {
          onChange(option?.value ?? "")
        }}
        onBlur={onBlur}
        onMenuOpen={() => {
          if (fieldRef.current) {
            setMenuWidth(fieldRef.current.getBoundingClientRect().width)
          }
        }}
        options={options}
        placeholder={placeholder}
        noOptionsMessage={() => t("No options")}
        classNamePrefix={classNamePrefix}
        styles={styleConfig}
        isSearchable={isSearchable}
        {...SELECT_MENU_PROPS}
      />
    </div>
  )
}

const FieldError = ({ message, show }) =>
  show && message ? (
    <FormFeedback className="d-block">{message}</FormFeedback>
  ) : null

const getInitialState = partner => {
  const existingPhoto = partner?.profilePhoto || partner?.photo || ""
  return {
    name: partner?.name || "",
    phone: partner?.phone ? normalizePhoneValue(partner.phone) : "",
    email: partner?.email || "",
    status: partner?.status || "Pending",
    reason: partner?.statusReason || "",
    branchId: partner?.branchId?._id || partner?.branchId || "",
    profilePhotoType: existingPhoto ? "URL" : "UPLOAD",
    profilePhoto: existingPhoto,
    profilePhotoFile: null,
  }
}

const getPhotoPreview = form => {
  if (form.profilePhotoType === "URL") {
    return form.profilePhoto || null
  }
  if (form.profilePhotoFile) {
    return URL.createObjectURL(form.profilePhotoFile)
  }
  return form.profilePhoto || null
}

const FIELD_VALIDATORS = {
  name: (form, { isEditMode }) => validateRiderName(form.name),
  phone: (form, { isEditMode }) =>
    isEditMode ? "" : validateRiderPhone(form.phone, { required: true }),
  email: form => validateRiderEmail(form.email),
  branchId: form => validateRiderBranch(form.branchId),
  reason: form => validateRiderStatusReason(form.status, form.reason),
  profilePhoto: form =>
    form.profilePhotoType === "URL"
      ? validateRiderProfilePhotoUrl(form.profilePhoto)
      : "",
}

const PartnerFormModal = ({ isOpen, toggle, mode = "create", partner, onSubmit, loading, onViewPendingOrders }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const { branchsList } = useSelector(state => state.Users)
  const { canFilterByBranch } = useCrossModuleFilterPermissions()
  const [form, setForm] = useState(getInitialState(partner))
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [profilePhotoValidating, setProfilePhotoValidating] = useState(false)
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0)
  const [loadingPendingCount, setLoadingPendingCount] = useState(false)

  const partnerId = partner?._id || partner?.id
  const isEditMode = mode === "edit"

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialState(partner))
      setErrors({})
      setTouched({})
      setProfilePhotoValidating(false)
      setPendingOrdersCount(0)
      if (canFilterByBranch) {
        dispatch(getAllBranchs())
      }
    }
  }, [isOpen, partner, dispatch, canFilterByBranch])

  useEffect(() => {
    if (!isOpen || form.status !== "Suspended" || !isEditMode || !partnerId) {
      setPendingOrdersCount(0)
      return
    }

    let cancelled = false
    const fetchPendingCount = async () => {
      setLoadingPendingCount(true)
      try {
        const response = await getRiderPendingOrdersCount(partnerId)
        if (!cancelled) {
          setPendingOrdersCount(response?.data?.count ?? 0)
        }
      } catch {
        if (!cancelled) setPendingOrdersCount(0)
      } finally {
        if (!cancelled) setLoadingPendingCount(false)
      }
    }

    fetchPendingCount()
    return () => {
      cancelled = true
    }
  }, [isOpen, form.status, isEditMode, partnerId])

  const language = i18n.language
  const hasBlockingPendingOrders =
    isEditMode && form.status === "Suspended" && pendingOrdersCount > 0

  const branchOptions = useMemo(
    () =>
      (canFilterByBranch ? branchsList || [] : []).map(item => ({
        value: item._id,
        label: language === "sa" ? item.nameAr || item.name : item.name,
      })),
    [branchsList, language, canFilterByBranch]
  )

  const statusOptions = useMemo(
    () => STATUS_OPTIONS.map(option => ({ value: option, label: t(option) })),
    [t]
  )

  const imageTypeOptions = useMemo(
    () => [
      { value: "URL", label: t("Image Url") },
      { value: "UPLOAD", label: t("Upload Image") },
    ],
    [t]
  )

  const validateField = (field, nextForm = form) => {
    const validator = FIELD_VALIDATORS[field]
    if (!validator) return ""
    return validator(nextForm, { isEditMode }) || ""
  }

  const touchField = field => {
    setTouched(prev => ({ ...prev, [field]: true }))
    setErrors(prev => ({ ...prev, [field]: validateField(field) }))
  }

  const handleChange = (key, value) => {
    let nextForm = { ...form, [key]: value }
    if (key === "status" && !REASON_REQUIRED_STATUSES.includes(value)) {
      nextForm = { ...nextForm, reason: "" }
    }
    setForm(nextForm)
    const fieldError = validateField(key, nextForm)
    if (touched[key] || errors[key]) {
      setErrors(prev => ({ ...prev, [key]: fieldError }))
    }
  }

  const handleProfilePhotoTypeChange = value => {
    const nextForm = {
      ...form,
      profilePhotoType: value,
      profilePhoto: value === "URL" ? form.profilePhoto : "",
      profilePhotoFile: null,
    }
    setForm(nextForm)
    if (touched.profilePhoto) {
      setErrors(prev => ({
        ...prev,
        profilePhoto: validateField("profilePhoto", nextForm),
      }))
    }
  }

  const touchProfilePhotoField = async (url = form.profilePhoto) => {
    setTouched(prev => ({ ...prev, profilePhoto: true }))

    const syncError = validateRiderProfilePhotoUrl(url)
    if (syncError || !String(url || "").trim()) {
      setProfilePhotoValidating(false)
      setErrors(prev => ({ ...prev, profilePhoto: syncError }))
      return
    }

    setErrors(prev => ({ ...prev, profilePhoto: "" }))
    setProfilePhotoValidating(true)
    const dimensionError = await validateRiderProfilePhotoUrlDimensions(url, {
      width: itemWidth,
      height: itemHeight,
    })
    setProfilePhotoValidating(false)
    setErrors(prev => ({ ...prev, profilePhoto: dimensionError }))
  }

  const handleSubmit = async () => {
    let nextErrors = validateRiderForm(form, { isEditMode })

    if (form.profilePhotoType === "URL" && String(form.profilePhoto || "").trim()) {
      const syncError = validateRiderProfilePhotoUrl(form.profilePhoto)
      if (syncError) {
        nextErrors = { ...nextErrors, profilePhoto: syncError }
      } else {
        setProfilePhotoValidating(true)
        const dimensionError = await validateRiderProfilePhotoUrlDimensions(
          form.profilePhoto,
          {
            width: itemWidth,
            height: itemHeight,
          }
        )
        setProfilePhotoValidating(false)
        if (dimensionError) {
          nextErrors = { ...nextErrors, profilePhoto: dimensionError }
        }
      }
    }

    setErrors(nextErrors)
    setTouched({
      name: true,
      phone: true,
      email: true,
      branchId: true,
      reason: REASON_REQUIRED_STATUSES.includes(form.status),
      profilePhoto: form.profilePhotoType === "URL",
    })

    if (hasRiderFormErrors(nextErrors)) {
      return
    }

    const data = new FormData()
    const existingPhoto = partner?.profilePhoto || partner?.photo || ""
    let photoType = form.profilePhotoType
    let photoUrl = form.profilePhoto.trim()

    data.append("name", form.name.trim())
    data.append("email", form.email.trim())
    data.append("status", form.status)
    if (REASON_REQUIRED_STATUSES.includes(form.status)) {
      data.append("reason", form.reason.trim())
    }
    data.append("branchId", form.branchId)

    if (photoType === "UPLOAD") {
      if (form.profilePhotoFile) {
        data.append("image", form.profilePhotoFile)
      } else if (isEditMode && existingPhoto) {
        photoType = "URL"
        photoUrl = existingPhoto
      }
    }

    data.append("profilePhotoType", photoType)
    if (photoType === "URL") {
      data.append("profilePhoto", photoUrl)
    }

    if (!isEditMode) {
      data.append("phone", String(form.phone || "").trim())
    }

    onSubmit(data)
  }

  const photoPreview = getPhotoPreview(form)
  const showError = field => Boolean(touched[field] && errors[field])
  const needsReason = REASON_REQUIRED_STATUSES.includes(form.status)

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        {isEditMode ? t("Edit Rider") : t("Add Rider")}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>
            {t("Full Name")} <span className="text-danger">*</span>
          </Label>
          <Input
            value={form.name}
            invalid={showError("name")}
            onChange={e => handleChange("name", e.target.value)}
            onBlur={() => touchField("name")}
            placeholder={t("Enter full name")}
          />
          <FieldError message={errors.name} show={showError("name")} />
        </FormGroup>

        <FormGroup>
          <Label>
            {t("Phone Number")} {!isEditMode && <span className="text-danger">*</span>}
          </Label>
          <PhoneNumberField
            name="phone"
            id="rider-phone"
            value={form.phone}
            onChange={value => handleChange("phone", value)}
            onBlur={() => touchField("phone")}
            readOnly={isEditMode}
            placeholder={t("Phone Number")}
            hasError={showError("phone")}
          />
          {isEditMode && (
            <small className="text-muted">{t("Phone cannot be changed in edit mode.")}</small>
          )}
          <FieldError message={errors.phone} show={showError("phone")} />
        </FormGroup>

        <FormGroup>
          <Label>{t("Email")}</Label>
          <Input
            type="email"
            value={form.email}
            invalid={showError("email")}
            maxLength={EMAIL_TOTAL_MAX_LENGTH}
            onChange={e => handleChange("email", e.target.value)}
            onBlur={() => touchField("email")}
            placeholder={t("Enter email")}
          />
          <FieldError message={errors.email} show={showError("email")} />
        </FormGroup>

        <FormGroup>
          <Label>
            {t("Branch")} <span className="text-danger">*</span>
          </Label>
          <RiderFormSelect
            value={form.branchId}
            onChange={value => {
              handleChange("branchId", value)
              if (value) {
                setTouched(prev => ({ ...prev, branchId: true }))
                setErrors(prev => ({ ...prev, branchId: "" }))
              }
            }}
            onBlur={() => touchField("branchId")}
            options={branchOptions}
            placeholder={t("Select Branch")}
            classNamePrefix="rider-branch-select"
            isSearchable
            hasError={showError("branchId")}
          />
          <FieldError message={errors.branchId} show={showError("branchId")} />
        </FormGroup>

        <FormGroup>
          <Label>{t("Status")}</Label>
          <RiderFormSelect
            value={form.status}
            onChange={value => handleChange("status", value)}
            options={statusOptions}
            placeholder={t("Select Status")}
            classNamePrefix="rider-status-select"
            isSearchable
          />
        </FormGroup>

        {needsReason && (
          <FormGroup>
            <Label>
              {t("Reason")} <span className="text-danger">*</span>
            </Label>
            {form.status === "Suspended" && isEditMode && (
              <div className="alert alert-warning py-2 px-3 mb-2">
                {loadingPendingCount ? (
                  <span>{t("Checking assigned orders...")}</span>
                ) : pendingOrdersCount > 0 ? (
                  <span>
                    {t("This rider has")}{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline fw-semibold"
                      onClick={() => onViewPendingOrders?.(partner)}
                    >
                      {t("{{count}} undelivered order(s)", { count: pendingOrdersCount })}
                    </button>{" "}
                    {t("assigned. Reassign them to another rider before suspending.")}
                  </span>
                ) : (
                  <span>{t("No undelivered orders are assigned to this rider.")}</span>
                )}
              </div>
            )}
            <Input
              type="textarea"
              rows={3}
              value={form.reason}
              invalid={showError("reason")}
              maxLength={RIDER_STATUS_REASON_MAX_LENGTH}
              onChange={e => handleChange("reason", e.target.value)}
              onBlur={() => touchField("reason")}
              placeholder={t("Enter reason for {{status}}...", {
                status: t(form.status).toLowerCase(),
              })}
            />
            <FieldError message={errors.reason} show={showError("reason")} />
          </FormGroup>
        )}

        <FormGroup>
          <Label>{t("Image Type")}</Label>
          <RiderFormSelect
            value={form.profilePhotoType}
            onChange={handleProfilePhotoTypeChange}
            options={imageTypeOptions}
            placeholder={t("Select Image Type")}
            classNamePrefix="rider-image-type-select"
          />
        </FormGroup>

        {form.profilePhotoType === "URL" ? (
          <FormGroup>
            <Label>{t("Profile Photo URL")}</Label>
            <Input
              value={form.profilePhoto}
              invalid={showError("profilePhoto")}
              maxLength={RIDER_PROFILE_URL_MAX_LENGTH}
              onChange={e => handleChange("profilePhoto", e.target.value)}
              onBlur={() => touchProfilePhotoField(form.profilePhoto)}
              placeholder="https://..."
              disabled={profilePhotoValidating}
            />
            <small className="text-muted">
              {t("Recommended dimensions: {{itemWidth}}x{{itemHeight}} pixels", {
                itemWidth,
                itemHeight,
              })}
            </small>
            {profilePhotoValidating && (
              <small className="text-muted d-block mt-1">
                {t("Validating image dimensions...")}
              </small>
            )}
            <FieldError message={errors.profilePhoto} show={showError("profilePhoto")} />
          </FormGroup>
        ) : (
          <FormGroup>
            <Label>{t("Upload Image")}</Label>
            <TranslatedFileInput
              id="riderProfilePhotoFile"
              name="profilePhotoFile"
              accept="image/jpeg, image/jpg, image/png, image/webp"
              onChange={e => {
                handleFileChange(
                  e,
                  "profilePhotoFile",
                  (_fieldName, file) => handleChange("profilePhotoFile", file),
                  itemWidth,
                  itemHeight,
                  itemMaxSize
                )
              }}
            />
            <small className="text-muted d-block mt-2">
              {t(
                "Recommended dimensions: Square Image and Size should be less than or equal to {{size}} KB",
                { size: itemMaxSize }
              )}
            </small>
          </FormGroup>
        )}

        {photoPreview && (
          <div className="mt-2">
            <img
              src={photoPreview}
              alt={t("Preview")}
              className="rounded border"
              style={{ width: 96, height: 96, objectFit: "cover" }}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={loading}>
          {t("Cancel")}
        </Button>
        <Button
          className="btn btn-primary ms-2"
          onClick={handleSubmit}
          disabled={
            loading ||
            profilePhotoValidating ||
            hasBlockingPendingOrders ||
            loadingPendingCount
          }
        >
          {loading
            ? t("Saving...")
            : isEditMode
            ? t("Update Rider")
            : t("Create Rider")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default PartnerFormModal
